import { useMutation, useQueryClient, type UseMutationResult } from "@tanstack/react-query";
import { useAuth } from "@/auth/AuthContext";
import { createApiClient } from "@/api/client";
import { queryKeys } from "@/api/queryClient";
import type { ProviderRoutingInfo, SlotTargetInput } from "@/types";

// #243: Provider-Routing setzen/löschen. Mit agentId → per-Agent (owner+admin), ohne → Plattform (admin).

function useClient() {
  const { session, logout } = useAuth();
  return () => createApiClient({ getToken: () => session?.accessToken ?? null, onUnauthorized: logout });
}
function keyFor(agentId?: string) {
  return agentId ? queryKeys.subResource("agents", agentId, "provider-routing") : queryKeys.list("provider-routing");
}
function pathFor(agentId?: string) {
  return agentId ? `/agents/${agentId}/provider-routing` : "/provider-routing/platform";
}

// #P001 (UC-2 Review LOW-7): ein Slot-Wechsel ändert auch die wählbare Modell-Liste des Agenten
// (`GET /agents/:id/models`, Picker an der Einrichtung) — beide Keys, sonst hinge der Picker
// bis zum staleTime am alten Stand.
function invalidateRouting(qc: ReturnType<typeof useQueryClient>, agentId?: string): void {
  void qc.invalidateQueries({ queryKey: keyFor(agentId) });
  if (agentId) void qc.invalidateQueries({ queryKey: queryKeys.subResource("agents", agentId, "models") });
}

export interface SetProviderRoutingInput {
  provider: string;
  baseUrl: string | null;
  credential: string;
  // #243: Auth-Position beim Ziel-Provider; null = Provider-Standard (Normalfall).
  authHeader?: string | null;
  authScheme?: string | null;
  // #307: Slot → Modellname (Kurzform) ODER Slot → eigenes Ziel (Anbieter/Key/URL/Auth/Kontext).
  mapping: Record<string, SlotTargetInput>;
}

export function useSetProviderRouting(
  agentId?: string,
): UseMutationResult<ProviderRoutingInfo, Error, SetProviderRoutingInput> {
  const qc = useQueryClient();
  const getClient = useClient();
  return useMutation({
    mutationFn: (input) => getClient().put<ProviderRoutingInfo>(pathFor(agentId), input),
    onSuccess: () => invalidateRouting(qc, agentId),
  });
}

// #P001 (UC-1): DELETE ist kein „deaktivieren" mehr, sondern das ENDGÜLTIGE Löschen des Routings
// (purge) — samt Gateway-Deployments. Ein Routing-weites Aus gibt es nicht mehr; ausschalten
// passiert pro Slot im Mapping. Der Master lehnt mit 409 SLOT_IN_USE ab, wenn noch etwas auf
// einen der Slots zeigt.
export function useDeleteProviderRouting(agentId?: string): UseMutationResult<{ ok: boolean }, Error, void> {
  const qc = useQueryClient();
  const getClient = useClient();
  return useMutation({
    mutationFn: () => getClient().delete<{ ok: boolean }>(pathFor(agentId)),
    onSuccess: () => invalidateRouting(qc, agentId),
  });
}

// #243: nur das Credential entfernen (Routing bleibt). Eigene Aktion, weil ein leeres Feld im
// Formular „unverändert" bedeutet — Muster wie DELETE /connectors/:id/connect (#231).
export function useClearProviderCredential(agentId?: string): UseMutationResult<ProviderRoutingInfo, Error, void> {
  const qc = useQueryClient();
  const getClient = useClient();
  return useMutation({
    mutationFn: () => getClient().delete<ProviderRoutingInfo>(`${pathFor(agentId)}/credential`),
    onSuccess: () => invalidateRouting(qc, agentId),
  });
}
